import { DeleteOutlined, HeartFilled, HeartOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import clsx from 'clsx';
import Confirm from 'common/components/confirm.component';
import PopoverApp from 'common/components/popover.component';
import { CatalogTranscription, GeneralTranscription, UserTranscription } from 'config/constants/transcription.constant';
import UserService from 'config/services/user.service';
import AuthIntentUtils from 'config/utils/auth-intent.util';
import CatalogUtils, { CardControlScope } from 'config/utils/catalog.util';
import { getErrorMessage, isAuthError } from 'config/utils/errors.util';
import { SnackbarUtilities } from 'config/utils/snackbar.util';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AnyAction, Dispatch } from 'redux';
import { store } from 'store/index';
import { CartItemAction, CartItemProduct, Item } from 'store/models/cart.model';
import { Product, ProductVariant } from 'store/models/catalog.model';
import { User } from 'store/models/user.model';
import { UserSlice } from 'store/slices/user.slice';
import styles from './style.module.scss';

type FavoriteVariant = 'button' | 'icon';

type CardControlsProps = {
  favorites: string[];
  product: Product | CartItemProduct;
  cartItems: Item[];
  isAuthenticated: boolean;
  withFavoriteButton?: boolean;
  favoriteVariant?: FavoriteVariant;
  withCartControls?: boolean;
  withQuantityContainerSmall?: boolean;
  hasStock?: boolean;
  selectedVariant?: ProductVariant;
  scope?: CardControlScope;
  userProfile: User;
};

const CardControls: React.FC<CardControlsProps> = ({
  favorites = [],
  product,
  cartItems = [],
  isAuthenticated = false,
  withFavoriteButton = false,
  favoriteVariant = 'button',
  withCartControls = true,
  withQuantityContainerSmall = false,
  hasStock = false,
  selectedVariant = {},
  scope = CardControlScope.PLP,
}) => {
  const dispatch: Dispatch<AnyAction> = useDispatch();
  const [inCart, setInCart] = useState<boolean>(false);
  const [currentItem, setCurrentItem] = useState<Item>();
  const [popoverIsOpen, setPopoverIsOpen] = useState<boolean>(false);
  const inFavorites = CatalogUtils.checkProductInFavorites(product, favorites);

  const applyFavoriteChange = async (productFav: Product | CartItemProduct, wantsFavorite: boolean): Promise<void> => {
    const userState = store.getState().user;
    const currentFavorites = userState.userProfile.favorites ?? [];
    const currentlyFavorite = CatalogUtils.checkProductInFavorites(productFav, currentFavorites);
    if (currentlyFavorite === wantsFavorite) return;

    const previousFavorites = currentFavorites;
    const newFavorites =
      wantsFavorite ?
        [...currentFavorites, `${productFav._id}`]
      : currentFavorites.filter((favorite: string) => favorite !== productFav._id);

    dispatch(UserSlice.refreshUserFavorites(newFavorites));

    try {
      const addFavoriteResult = await UserService.updateUserProfile({ ...userState.userProfile, favorites: newFavorites });
      if (addFavoriteResult) {
        SnackbarUtilities.success(UserTranscription.FAVORITE_UPDATED);
      } else {
        dispatch(UserSlice.refreshUserFavorites(previousFavorites));
      }
    } catch (e: unknown) {
      dispatch(UserSlice.refreshUserFavorites(previousFavorites));
      if (isAuthError(e)) throw e;
      SnackbarUtilities.error(getErrorMessage(e, UserTranscription.ERROR_UPDATING_USER_PROFILE));
    }
  };

  const handleAddToFavorites = (productFav: Product | CartItemProduct, isUserAuthenticated: boolean, inFavorites: boolean): void => {
    const wantsFavorite = !inFavorites;
    const run = () => applyFavoriteChange(productFav, wantsFavorite);

    if (!isUserAuthenticated) {
      AuthIntentUtils.requireAuth({ action: run });
      return;
    }

    AuthIntentUtils.withAuthRetry(run);
  };

  const addToCart = (
    targetProduct: Product | CartItemProduct,
    action: CartItemAction,
    currentCartItems: Item[],
    targetVariant: ProductVariant,
  ) => {
    const foundProduct = CatalogUtils.findCartItem(targetProduct, targetVariant, currentCartItems);

    !foundProduct ?
      CatalogUtils.addToCart(targetProduct as Product, targetVariant)
    : CatalogUtils.increaseOrDecreaseItemInCart(targetProduct, action, targetVariant);
  };

  const renderControlChanger = (
    productChanger: Product | CartItemProduct,
    currentItem: Item | undefined,
    withQuantityContainerSmall: boolean,
    selectedVariant: ProductVariant,
  ): React.ReactNode => {
    const { quantity } = currentItem ?? {};

    return (
      <div className={styles['control-changer-container']}>
        {quantity === 1 && (
          <Confirm
            placement='topRight'
            title={`${GeneralTranscription.DELETE} ${productChanger.name}`}
            description={CatalogTranscription.CONFIRM_TO_DELETE_FROM_CART}
            onConfirm={() => CatalogUtils.removeToCart(productChanger, selectedVariant)}
            key={CatalogTranscription.CONFIRM_TO_DELETE_FROM_CART}
          >
            <Button type='text' className={styles['qty-button']} icon={<DeleteOutlined />} />
          </Confirm>
        )}
        {quantity && quantity > 1 && (
          <Button
            type='text'
            className={styles['qty-button']}
            icon={<MinusOutlined />}
            onClick={() => CatalogUtils.increaseOrDecreaseItemInCart(productChanger, CartItemAction.REMOVE_ITEM, selectedVariant)}
          />
        )}
        <div className={clsx(styles['item-quantity'], withQuantityContainerSmall && styles['small'])}>{quantity} Un.</div>
        <Button
          type='text'
          className={styles['qty-button']}
          icon={<PlusOutlined />}
          disabled={quantity === selectedVariant.stock}
          onClick={() => CatalogUtils.increaseOrDecreaseItemInCart(productChanger, CartItemAction.ADD_ITEM, selectedVariant)}
        />
      </div>
    );
  };

  const renderAddButton = (
    targetProduct: Product | CartItemProduct,
    stockAvailable: boolean,
    targetVariant: ProductVariant,
  ): React.ReactNode => {
    if (scope === CardControlScope.PDP) {
      return (
        <div className={styles['add-to-cart-button']}>
          <Button
            type='primary'
            block
            onClick={() => addToCart(targetProduct, CartItemAction.ADD_ITEM, cartItems, targetVariant)}
            disabled={!stockAvailable}
          >
            {CatalogTranscription.ADD_TO_CART}
          </Button>
        </div>
      );
    }

    const { variant } = targetProduct as Product;
    const popoverContent = (
      <div className={styles['product-variant-selection-window']}>
        {variant?.map((v, index) => {
          const variantInCart = CatalogUtils.checkProductInCart(targetProduct, v);
          const variantOutOfStock = v.stock === 0;
          return (
            <Button
              type='text'
              key={index}
              disabled={variantInCart || variantOutOfStock}
              onClick={() => {
                addToCart(targetProduct, CartItemAction.ADD_ITEM, cartItems, v);
                setPopoverIsOpen(false);
              }}
              className={clsx(
                styles['variant-option'],
                variantInCart && styles['variant-in-cart'],
                variantOutOfStock && styles['variant-out-of-stock'],
              )}
            >
              {v.option}
              {variantInCart && ` - ${CatalogTranscription.ADDED_TO_CART}`}
              {variantOutOfStock && ` - ${CatalogTranscription.OUT_OF_STOCK_VARIANT}`}
            </Button>
          );
        })}
      </div>
    );

    return (
      <div className={styles['add-to-cart-button']}>
        <PopoverApp isOpen={popoverIsOpen} title='Elige una opción' content={popoverContent}>
          <Button type='primary' block onClick={() => setPopoverIsOpen(true)} disabled={!stockAvailable}>
            {CatalogTranscription.ADD_TO_CART}
          </Button>
        </PopoverApp>
      </div>
    );
  };

  const renderFavoriteButton = (
    targetProduct: Product | CartItemProduct,
    isUserAuthenticated: boolean,
    isFavorite: boolean,
    stockAvailable: boolean,
  ): React.ReactNode => {
    if (favoriteVariant === 'icon') {
      return (
        <Button
          type='text'
          className={clsx(styles['favorite-icon-button'], isFavorite && styles['favorite-icon-active'])}
          icon={isFavorite ? <HeartFilled /> : <HeartOutlined />}
          onClick={(event) => {
            event.stopPropagation();
            handleAddToFavorites(targetProduct, isUserAuthenticated, isFavorite);
          }}
          aria-label={isFavorite ? CatalogTranscription.REMOVE_FROM_FAVORITES : CatalogTranscription.ADD_TO_FAVORITES}
        />
      );
    }

    return (
      <div className={styles['add-to-favorites-button']}>
        <Button block onClick={() => handleAddToFavorites(targetProduct, isUserAuthenticated, isFavorite)} disabled={!stockAvailable}>
          {isFavorite ? CatalogTranscription.REMOVE_FROM_FAVORITES : CatalogTranscription.ADD_TO_FAVORITES}
        </Button>
      </div>
    );
  };

  useEffect(() => {
    const productInCart = CatalogUtils.checkProductInCart(product, selectedVariant);
    const productCartItem = CatalogUtils.findCartItem(product, selectedVariant, cartItems);

    setInCart(productInCart);
    setCurrentItem(productCartItem);
  }, [cartItems, favorites, selectedVariant, product]);

  const stopCardNavigation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <div
      className={clsx(
        styles['cart-controls-container'],
        withQuantityContainerSmall && styles['fix-width'],
        !withCartControls && styles['icon-only'],
        withCartControls && withFavoriteButton && favoriteVariant === 'icon' && styles['with-icon-favorite'],
      )}
      onClick={stopCardNavigation}
    >
      {withCartControls &&
        (inCart ?
          renderControlChanger(product, currentItem, withQuantityContainerSmall, selectedVariant)
        : renderAddButton(product, hasStock, selectedVariant))}
      {withFavoriteButton && renderFavoriteButton(product, isAuthenticated, inFavorites, hasStock)}
    </div>
  );
};

export default CardControls;
